import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { supabase } from "../lib/supabase";
import { readFunctionErrorBody } from "../lib/functionError";
import { colors, fontSize, fontWeight, radius, shadow, space } from "../lib/tokens";
import type { TenantDetail } from "../hooks/useTenant";
import { SUPPORTED_TIMEZONES, missingTenantIdentityFields } from "@klubora/shared";
import { LegalIdentityPill } from "./LegalIdentityPill";

interface EditTenantIdentityModalProps {
  tenant: TenantDetail;
  onClose: () => void;
  onSaved: () => void;
}

type EditableField =
  | "name"
  | "legal_name"
  | "support_email"
  | "support_whatsapp"
  | "legal_cnpj"
  | "billing_email"
  | "legal_address"
  | "legal_jurisdiction"
  | "privacy_contact"
  | "locale"
  | "timezone"
  | "currency";

type FormState = Record<EditableField, string>;

const REASON_LABELS: Record<string, string> = {
  required: "Campo obrigatório.",
  too_long: "Texto muito longo.",
  invalid_email: "Email inválido.",
  must_be_14_digits: "CNPJ deve ter exatamente 14 dígitos (sem formatação).",
  invalid_locale: "Locale deve ter formato BCP-47 (ex.: pt-BR).",
  must_be_iso4217: "Moeda deve ter 3 letras maiúsculas (ex.: BRL).",
  invalid_timezone: "Timezone não suportado.",
};

const FIELD_LABELS: Record<EditableField, string> = {
  name: "Nome",
  legal_name: "Razão social",
  support_email: "Email de contato",
  support_whatsapp: "WhatsApp de suporte",
  legal_cnpj: "CNPJ",
  billing_email: "Email de cobrança",
  legal_address: "Endereço da sede",
  legal_jurisdiction: "Comarca do foro",
  privacy_contact: "Contato do encarregado de dados",
  locale: "Locale",
  timezone: "Timezone",
  currency: "Moeda",
};

function toForm(t: TenantDetail): FormState {
  return {
    name: t.name,
    legal_name: t.legal_name ?? "",
    support_email: t.support_email ?? "",
    support_whatsapp: t.support_whatsapp ?? "",
    legal_cnpj: t.legal_cnpj ?? "",
    billing_email: t.billing_email ?? "",
    legal_address: t.legal_address ?? "",
    legal_jurisdiction: t.legal_jurisdiction ?? "",
    privacy_contact: t.privacy_contact ?? "",
    locale: t.locale,
    timezone: t.timezone,
    currency: t.currency,
  };
}

function diffPatch(initial: FormState, current: FormState): Partial<Record<EditableField, string | null>> {
  const patch: Partial<Record<EditableField, string | null>> = {};
  (Object.keys(current) as EditableField[]).forEach((key) => {
    const before = initial[key].trim();
    const after = current[key].trim();
    if (before === after) return;
    patch[key] = after === "" ? null : after;
  });
  return patch;
}

export function EditTenantIdentityModal({ tenant, onClose, onSaved }: EditTenantIdentityModalProps) {
  const initial = useMemo(() => toForm(tenant), [tenant]);
  const [form, setForm] = useState<FormState>(initial);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Phase 2F.1 — `name` is mobile-baked. During the lock period
  // (mobile_assets_ready_at set, mobile_published_at null) the server
  // rejects name patches with 403 mobile_build_locked. Disable the input
  // here and show an inline note so users see the lock reason up-front.
  const [nameLocked, setNameLocked] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc("is_mobile_build_locked", { p_tenant_id: tenant.id });
      if (!cancelled) setNameLocked(data === true);
    })();
    return () => { cancelled = true; };
  }, [tenant.id]);

  const patch = useMemo(() => diffPatch(initial, form), [initial, form]);
  const hasChanges = Object.keys(patch).length > 0;

  // KLUB-185 — o servidor recusa QUALQUER save desta ficha enquanto o conjunto
  // jurídico resultante estiver incompleto, inclusive um save só de WhatsApp.
  // A tela conta pela MESMA lista (importada de `@klubora/shared`, espelhada em
  // `_shared/tenant-identity-completeness.ts`) para não prometer um salvamento
  // que a borda vai negar.
  const legalMissing = useMemo(() => missingTenantIdentityFields(form), [form]);
  const legalIncomplete = legalMissing.length > 0;

  const setField = (field: EditableField) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const setTimezone = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setForm((f) => ({ ...f, timezone: e.target.value }));

  const handleSubmit = async () => {
    if (!hasChanges) return;
    setSaving(true);
    setErrorMsg(null);
    const { data, error } = await supabase.functions.invoke("platform-update-tenant-identity", {
      body: { tenantId: tenant.id, fields: patch },
    });
    setSaving(false);

    // A recusa vem como 400, e o supabase-js entrega 400 como `error` com
    // `data: null` — o corpo, onde estão o código e a lista de faltantes, só
    // existe em `error.context`. Ler `data.error` sozinho torna todo o
    // tratamento abaixo inalcançável e devolve "non-2xx status code" ao
    // operador. Ver `lib/functionError.ts`.
    type ErrorBody = {
      error?: string;
      field?: EditableField;
      reason?: string;
      fields?: string[];
    };
    const payload = error
      ? await readFunctionErrorBody<ErrorBody>(error)
      : (data as ErrorBody | null);

    if (error && !payload?.error) {
      setErrorMsg(error.message || "Falha ao salvar.");
      return;
    }
    if (payload?.error) {
      if (payload.error === "identity_incomplete") {
        // O servidor nomeia TODOS os faltantes; repassar a lista inteira evita
        // o vai-e-vem de descobrir um campo por save.
        const faltantes = payload.fields?.join(", ") ?? "campos obrigatórios";
        setErrorMsg(`Cadastro jurídico incompleto: ${faltantes}.`);
        return;
      }
      if (payload.error === "invalid_field" && payload.field && payload.reason) {
        const fieldLabel = FIELD_LABELS[payload.field] ?? payload.field;
        const reasonLabel = REASON_LABELS[payload.reason] ?? payload.reason;
        setErrorMsg(`${fieldLabel}: ${reasonLabel}`);
      } else {
        setErrorMsg(`Erro: ${payload.error}`);
      }
      return;
    }
    onSaved();
    onClose();
  };

  return (
    <div style={s.backdrop}>
      <div style={s.card}>
        <h2 style={s.title}>Editar identidade do tenant</h2>
        <p style={s.subtitle}>Slug <code style={s.code}>{tenant.slug}</code> não é editável.</p>

        <Section label="Identidade">
          <Field
            label="Nome"
            required
            value={form.name}
            onChange={setField("name")}
            disabled={nameLocked}
            note={nameLocked ? "Bloqueado: tenant em publicação mobile. Edição do nome retorna após app go-live nas duas lojas." : undefined}
          />
          <Field label="Email de contato" type="email" value={form.support_email} onChange={setField("support_email")} />
          <Field label="WhatsApp de suporte" placeholder="+5511..." value={form.support_whatsapp} onChange={setField("support_whatsapp")} />
        </Section>

        {/* KLUB-185 — os seis campos num bloco só, com o que falta contado no
            cabeçalho. Eles são um CONJUNTO: a fatura B2B lê três deles e a
            publicação dos textos legais lê os outros três, e nenhum save da
            ficha passa com qualquer um vazio. Separá-los em duas seções faria
            parecer que uma metade é opcional. */}
        <Section
          label="Cadastro jurídico & faturamento"
          aside={<LegalIdentityPill missing={legalMissing} />}
        >
          <Field label="Razão social" required value={form.legal_name} onChange={setField("legal_name")} invalid={form.legal_name.trim() === ""} />
          <Field label="CNPJ" required placeholder="14 dígitos, sem formatação" value={form.legal_cnpj} onChange={setField("legal_cnpj")} invalid={form.legal_cnpj.trim() === ""} />
          <Field label="Endereço da sede" required fullWidth placeholder="Rua, número, bairro, cidade/UF, CEP" value={form.legal_address} onChange={setField("legal_address")} invalid={form.legal_address.trim() === ""} />
          <Field label="Comarca do foro" required placeholder="São Paulo/SP" value={form.legal_jurisdiction} onChange={setField("legal_jurisdiction")} invalid={form.legal_jurisdiction.trim() === ""} />
          <Field label="Encarregado de dados" required placeholder="privacidade@clube.com.br" value={form.privacy_contact} onChange={setField("privacy_contact")} invalid={form.privacy_contact.trim() === ""} />
          <Field label="Email de cobrança" required type="email" value={form.billing_email} onChange={setField("billing_email")} invalid={form.billing_email.trim() === ""} />
        </Section>

        <Section label="Localização">
          <Field label="Locale" required placeholder="pt-BR" value={form.locale} onChange={setField("locale")} />
          <SelectField label="Timezone" required value={form.timezone} onChange={setTimezone} options={SUPPORTED_TIMEZONES} />
          <Field label="Moeda (ISO-4217)" required placeholder="BRL" value={form.currency} onChange={setField("currency")} />
        </Section>

        {errorMsg && <div style={s.errorText}>{errorMsg}</div>}
        {!errorMsg && legalIncomplete && (
          <div style={s.warnText}>
            Cadastro jurídico incompleto: {legalMissing.join(", ")}. Nenhuma alteração desta ficha
            é salva enquanto faltarem.
          </div>
        )}

        <div style={s.actions}>
          <button onClick={onClose} disabled={saving} style={s.cancelBtn}>Cancelar</button>
          <button
            onClick={handleSubmit}
            disabled={saving || !hasChanges || legalIncomplete}
            style={saving || !hasChanges || legalIncomplete ? { ...s.submitBtn, ...s.submitBtnOff } : s.submitBtn}
          >
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ label, children, aside }: { label: string; children: React.ReactNode; aside?: React.ReactNode }) {
  return (
    <div style={s.section}>
      <div style={s.sectionHead}>
        <div style={s.sectionLabel}>{label}</div>
        {aside}
      </div>
      <div style={s.fieldGrid}>{children}</div>
    </div>
  );
}


function Field({
  label,
  value,
  onChange,
  required,
  placeholder,
  type = "text",
  disabled,
  note,
  invalid,
  fullWidth,
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  note?: string;
  invalid?: boolean;
  fullWidth?: boolean;
}) {
  const inputStyle: CSSProperties = {
    ...s.input,
    ...(invalid ? { borderColor: colors.error } : null),
    ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : null),
  };
  return (
    <label style={fullWidth ? { ...s.fieldLabel, gridColumn: "1 / -1" } : s.fieldLabel}>
      <span style={s.fieldLabelText}>
        {label}
        {required && <span style={s.required}> *</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        style={inputStyle}
      />
      {note && <span style={s.fieldNote}>{note}</span>}
      {invalid && !note && <span style={s.fieldError}>Obrigatório.</span>}
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  required,
  options,
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  required?: boolean;
  options: readonly string[];
}) {
  return (
    <label style={s.fieldLabel}>
      <span style={s.fieldLabelText}>
        {label}
        {required && <span style={s.required}> *</span>}
      </span>
      <select value={value} onChange={onChange} style={s.input}>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </label>
  );
}

const s: Record<string, CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(14, 17, 22, 0.4)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: space.lg,
    overflowY: "auto",
  },
  card: {
    background: colors.white,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.md,
    boxShadow: shadow.lg,
    padding: space.xxl,
    width: 560,
    maxWidth: "100%",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  title: {
    margin: `0 0 ${space.xs}px 0`,
    fontSize: fontSize.xl,
    fontWeight: fontWeight.semibold,
    color: colors.ink,
  },
  subtitle: {
    margin: `0 0 ${space.lg}px 0`,
    fontSize: fontSize.sm,
    color: colors.stone,
  },
  code: {
    fontFamily: "JetBrains Mono, monospace",
    fontSize: fontSize.sm,
    background: colors.paper,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.xs,
    padding: "1px 6px",
  },
  section: {
    marginBottom: space.lg,
  },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space.sm,
  },
  sectionLabel: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.medium,
    color: colors.stone,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: space.sm,
  },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: space.md,
  },
  fieldLabel: {
    display: "flex",
    flexDirection: "column",
    gap: space.xs,
    fontSize: fontSize.sm,
  },
  fieldLabelText: {
    color: colors.stone,
    fontWeight: fontWeight.medium,
  },
  required: {
    color: colors.error,
  },
  input: {
    padding: space.sm,
    fontSize: fontSize.base,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    background: colors.white,
    color: colors.ink,
    fontFamily: "inherit",
  },
  fieldNote: {
    fontSize: fontSize.xs,
    color: colors.warning,
    marginTop: space.xs,
    lineHeight: 1.4,
  },
  fieldError: {
    fontSize: fontSize.xs,
    color: colors.error,
    marginTop: space.xs,
    lineHeight: 1.4,
  },
  warnText: {
    color: colors.warning,
    fontSize: fontSize.sm,
    marginTop: space.sm,
    background: colors.warningSoft,
    padding: space.sm,
    borderRadius: radius.sm,
    lineHeight: 1.5,
  },
  errorText: {
    color: colors.error,
    fontSize: fontSize.sm,
    marginTop: space.sm,
    background: colors.errorSoft,
    padding: space.sm,
    borderRadius: radius.sm,
  },
  actions: {
    display: "flex",
    gap: space.sm,
    justifyContent: "flex-end",
    marginTop: space.lg,
  },
  cancelBtn: {
    padding: `${space.sm}px ${space.lg}px`,
    border: `1px solid ${colors.fog}`,
    background: "transparent",
    borderRadius: radius.sm,
    color: colors.ink,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    cursor: "pointer",
  },
  submitBtn: {
    padding: `${space.sm}px ${space.lg}px`,
    background: colors.indigo,
    color: colors.white,
    border: "none",
    borderRadius: radius.sm,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    cursor: "pointer",
  },
  submitBtnOff: {
    background: colors.fog,
    color: colors.stone,
    cursor: "not-allowed",
  },
};
